/* eslint-disable local/no-localstorage-in-react, no-restricted-syntax --
 * FT-365 v2: this helper is the ONE place that intentionally reaches for
 * localStorage directly. Its whole purpose is a raw whitelist-based purge of
 * every LS/IDB cache derived from the previous user's session on sign-out or
 * user-boundary detection. `useStorageState` / `useCollections` are React
 * hooks that CANNOT be used here (called from AuthGate.logout / checkAuthState
 * outside React render), and even if usable they hydrate + write back —
 * the opposite of what this helper needs to do.
 */
/**
 * FT-365 v2: authCacheWipe — cross-user cache purge on logout / user-boundary switch.
 *
 * Purpose: after sign-out (or on Layer B user-boundary detection), erase every
 * cache derived from the previous user's session so the next user's mount
 * begins from a known-empty baseline. This closes the cross-org login cache
 * leak that FT-365 v1 attempted and rolled back.
 *
 * Whitelist-based preservation (NOT org-scoped purge) — this is the fix for
 * the super_admin case where organizationId === null defeated v1's org-scoped
 * purge. See DESIGN-BRIEF §3 decisions #2, #11.
 *
 * Preserved (never touched):
 *   - localStorage: sr.outbox.v1 (regulatory FT-234), sr.migration.*,
 *     sr.setup.*, sr.feature.*
 *   - IndexedDB tables: db.photos, db.photoThumbs, db.syncQueue
 *
 * Nuked:
 *   - localStorage: auth tokens, impersonation state, any key whose prefix
 *     matches a MODULE_MAP module name (crew_, fleet_crew_, equipment_register_,
 *     drills_, maintenance_jobs_, etc.), plus known top-level auth keys
 *     (currentVesselId, vessels)
 *   - sessionStorage: full clear
 *   - Service-worker Cache API: all caches (guarded on typeof caches)
 *   - Dexie db.storage: all rows NOT in the preserve whitelist
 *   - Dexie db.auditLog: full clear (no orgId scoping — see §3 decision #6)
 *
 * Emits `sr:auth:cache-wiped` CustomEvent on completion for telemetry.
 * Never throws — logs individual step failures and continues.
 *
 * See FT-365-v2/DESIGN-BRIEF.md §5 (belt-and-braces layers) + §13
 * (implementation clarifications) for the full contract.
 */

import { MODULE_MAP } from '../sync/moduleMap';
import { db } from './db';
import { azureApiClient } from './azureApiClient';
import { devLog, devWarn } from '../utils/devLog';
import { track1 } from '../utils/track1Telemetry';

// FT-365 v3 Layer A#4: dynamic import kept out of module init so tests / SSR
// can execute this file without importing React hooks. Resolved lazily inside
// wipeUserCaches() below.
let cancelAllPendingFlushesAndBlockImpl = null;
try {
  // eslint-disable-next-line global-require
  cancelAllPendingFlushesAndBlockImpl = require('../hooks/useStorageState').cancelAllPendingFlushesAndBlock;
} catch (_) {
  // Test env or circular-import — resolved later via lazy import inside wipeUserCaches.
}

// FT-365 v3 Layer E: per-tab UUID stored in sessionStorage so BroadcastChannel
// posts can identify their own tab and skip self-echo. sessionStorage survives
// navigation within the same tab and is unique per tab.
const TAB_ID_KEY = 'sr.tab.id';
let tabId = null;
try {
  if (typeof sessionStorage !== 'undefined') {
    tabId = sessionStorage.getItem(TAB_ID_KEY);
    if (!tabId) {
      tabId = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `tab-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(TAB_ID_KEY, tabId);
    }
  }
} catch (_) {
  tabId = `tab-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

// FT-365 v3 Layer E: BroadcastChannel for cross-tab wipe coordination.
// Guarded on typeof so tests / older browsers don't blow up.
const WIPE_CHANNEL_NAME = 'sr-auth-boundary';
const WIPE_RECEIVED_KEY = 'sr.tab.wipe-received';
const WIPE_RECEIVED_DEBOUNCE_MS = 5000;

let wipeChannel = null;
try {
  if (typeof BroadcastChannel !== 'undefined') {
    wipeChannel = new BroadcastChannel(WIPE_CHANNEL_NAME);
  }
} catch (_) {
  wipeChannel = null;
}

// FT-365 Option B Phase 2: log BC support once at module load so observability
// records whether the cross-tab wipe fallback (storage event) is in play for
// this session. Fires whether or not track1 gate is on — the log call is a
// no-op when gate is off. Runs at module init, so init order matters: track1
// import above must resolve before this line executes.
try {
  track1.log('[BC-SUPPORT]', { available: typeof BroadcastChannel !== 'undefined' });
} catch (_) { /* non-fatal */ }

/**
 * FT-365 v3 Layer E#4: reload-loop prevention. Returns true if this tab reloaded
 * within the last WIPE_RECEIVED_DEBOUNCE_MS ms due to a cross-tab wipe — new
 * loads skip triggering another reload during that window.
 */
function isWipeReceivedRecent() {
  try {
    if (typeof sessionStorage === 'undefined') return false;
    const raw = sessionStorage.getItem(WIPE_RECEIVED_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return false;
    return (Date.now() - ts) < WIPE_RECEIVED_DEBOUNCE_MS;
  } catch (_) {
    return false;
  }
}

/**
 * FT-365 v3 Layer E: handle a wipe posted from another tab.
 * Cancel any pending flushes so nothing repolutes LS post-reload, then reload.
 * Guarded by isWipeReceivedRecent to prevent reload loops.
 */
async function handleForeignWipe(evtData) {
  try {
    if (!evtData || evtData.type !== 'user-boundary-wipe') return;
    // Ignore self-echo
    if (evtData.initiatingTabId === tabId) return;
    // Prevent reload loops
    if (isWipeReceivedRecent()) return;

    // FT-365 Option B Phase 2: observability event fires ONCE per genuine
    // cross-tab wipe reception (after self-echo + debounce filters). Source
    // discriminates BroadcastChannel path vs the storage-event fallback so
    // Safari-private / older-browser regressions are visible.
    try {
      track1.log('[FOREIGN-WIPE-RECEIVED]', {
        source: evtData.initiatingTabId === 'storage-event-fallback' ? 'storage-event' : 'BroadcastChannel',
        wipedByUserId: evtData.userId || null,
      });
    } catch (_) { /* non-fatal */ }

    // Cancel pending flushes before reload
    try {
      const cancel = await resolveCancelHelper();
      if (cancel) await cancel();
    } catch (err) {
      devWarn('[authCacheWipe] Layer E: cancelAllPendingFlushesAndBlock failed (non-fatal):', err);
    }

    try {
      sessionStorage.setItem(WIPE_RECEIVED_KEY, String(Date.now()));
    } catch (_) { /* non-fatal */ }

    try {
      window.location.reload();
    } catch (err) {
      devWarn('[authCacheWipe] Layer E: reload failed:', err);
    }
  } catch (err) {
    devWarn('[authCacheWipe] Layer E: handleForeignWipe crashed (non-fatal):', err);
  }
}

// FT-365 v3 Layer E#1/#2: subscribe to wipes from other tabs at module load.
try {
  if (wipeChannel) {
    wipeChannel.addEventListener('message', (e) => { handleForeignWipe(e.data); });
  }
  // FT-365 v3 Layer E#3: storage-event fallback for browsers without BroadcastChannel.
  // sr.auth.lastUserId is the LS marker written by AuthGate on user-boundary. When
  // it changes cross-tab, other tabs see a `storage` event.
  if (typeof window !== 'undefined') {
    window.addEventListener('storage', (e) => {
      if (e && e.key === 'sr.auth.lastUserId' && e.newValue !== e.oldValue) {
        handleForeignWipe({
          type: 'user-boundary-wipe',
          initiatingTabId: 'storage-event-fallback',
          ts: Date.now(),
        });
      }
    });
  }
} catch (_) { /* non-fatal */ }

/**
 * FT-365 v3 Layer A#4: resolve the useStorageState cancel helper lazily.
 * Direct require may return undefined during circular init; retry via dynamic import.
 */
async function resolveCancelHelper() {
  if (cancelAllPendingFlushesAndBlockImpl) return cancelAllPendingFlushesAndBlockImpl;
  try {
    const mod = await import('../hooks/useStorageState');
    cancelAllPendingFlushesAndBlockImpl = mod.cancelAllPendingFlushesAndBlock || null;
    return cancelAllPendingFlushesAndBlockImpl;
  } catch (_) {
    return null;
  }
}

/**
 * FT-365 v3 Layer B#4: SyncBridge in-flight fetch aborter. Same lazy-resolve
 * pattern as the useStorageState helper — avoids circular init issues.
 */
let abortAllInflightSyncImpl = null;
async function resolveAbortAllInflightSync() {
  if (abortAllInflightSyncImpl) return abortAllInflightSyncImpl;
  try {
    const mod = await import('../sync/UniversalSyncBridge');
    abortAllInflightSyncImpl = mod.abortAllInflightSync || null;
    return abortAllInflightSyncImpl;
  } catch (_) {
    return null;
  }
}

/**
 * FT-365 v3 Layer A#3: VesselConfigStore in-memory singleton reset.
 * The LS key is already covered by A#2 LEGACY_PREFIXES, but the in-memory
 * singleton retains stale state after wipe. Prefer `clear()` (removes the LS
 * key AND resets config to defaults) over `reset()` — the latter calls
 * `save()` internally which would re-write sr.vesselConfig.v1 to LS AFTER
 * purgeLocalStorage cleared it, defeating the wipe.
 */
async function resetVesselConfigStore() {
  try {
    const mod = await import('../state/vesselConfig.store');
    const store = mod && (mod.default || mod);
    if (store) {
      if (typeof store.clear === 'function') {
        store.clear();
        return true;
      }
      if (typeof store.reset === 'function') {
        store.reset();
        return true;
      }
    }
  } catch (_) { /* non-fatal — Layer D remount handles this */ }
  return false;
}

// LS keys removed unconditionally on logout — auth + impersonation surface.
const AUTH_KEYS = [
  'sms_pro_auth_token',
  'sms_pro_user',
  'sr.auth.session',
  'current_user',
  'currentVesselId',
  'vessels',
  'sr.working.targetOrgId',
  'sr.impersonation.targetOrgId',
  'sr.impersonation.session',
];

// FT-365 v3.4 preserveAuth: keep the currently-authenticated user's JWT
// and identity keys when wiping cross-org cached data. Layer B in AuthGate
// fires this wipe AFTER LoginScreen wrote the fresh JWT — nuking it here
// creates a token-less window that causes the next /api/* call to 401 and
// bounces the user to LoginScreen (see FT-365 v3.4 investigation).
// STRICTLY the auth-identity trio + JWT — NOT currentVesselId / vessels /
// sr.working.targetOrgId / sr.impersonation.*, all of which are user-specific
// and MUST still wipe on user-boundary transitions.
const AUTH_PRESERVE_KEYS = new Set([
  'sms_pro_auth_token',
  'sms_pro_user',
  'sr.auth.session',
  'current_user',
]);

// LS key prefixes preserved even when their base module would otherwise match.
// FT-234: outbox survives across logout so queued offline writes eventually flush.
// sr.migration.* — one-shot data-migration flags; wiping causes migrations to
// re-run and can corrupt data. sr.setup.* — first-run + admin bootstrap.
// sr.feature.* — feature flag state (org- and device-level; wiping resets UX).
// sr.auth.lastUserId is preserved so Layer B can compare on the next mount.
const PRESERVE_LS_PREFIXES = [
  'sr.outbox.',
  'sr.migration.',
  'sr.setup.',
  'sr.feature.',
];

const PRESERVE_LS_EXACT = new Set([
  'sr.auth.lastUserId',
]);

// IDB db.storage preservation whitelist — matches PRESERVE_LS_PREFIXES because
// storageRouter mirrors LS keys into IDB under the same names.
const PRESERVE_IDB_STORAGE_PREFIXES = [
  'sr.outbox.',
  'sr.migration.',
  'sr.setup.',
  'sr.feature.',
];

const PRESERVE_IDB_STORAGE_EXACT = new Set([
  'sr.auth.lastUserId',
]);

/**
 * Build the module-prefix allowlist from MODULE_MAP. Every top-level module key
 * (crew, fleet_crew, equipment_register, etc.) is scoped in LS as
 * `<module>_<orgId>_<vesselId>` or `<module>_<vesselId>`. Prefix match is done
 * against `key.startsWith(prefix + '_')` per §13.2 to avoid false positives
 * (e.g. `crew_scheduler_v1` matching bare `crew`).
 */
function getModulePrefixes() {
  return Object.keys(MODULE_MAP);
}

function isPreservedLSKey(key) {
  if (PRESERVE_LS_EXACT.has(key)) return true;
  for (const prefix of PRESERVE_LS_PREFIXES) {
    if (key.startsWith(prefix)) return true;
  }
  return false;
}

function isPreservedIDBStorageKey(key) {
  if (PRESERVE_IDB_STORAGE_EXACT.has(key)) return true;
  for (const prefix of PRESERVE_IDB_STORAGE_PREFIXES) {
    if (key.startsWith(prefix)) return true;
  }
  return false;
}

/**
 * Purge localStorage: auth keys unconditionally + every key matching a
 * MODULE_MAP prefix (with `_` separator per §13.2) + a defensive sweep for
 * any legacy key not covered by MODULE_MAP but clearly module-scoped
 * (e.g. photo/checklist/draft LS keys the whitelist wouldn't otherwise reach).
 *
 * FT-365 v3.4: preserveAuth keeps the just-issued JWT + user + session +
 * current_user so getVessels() (fired immediately after AuthGate Layer B) can
 * still send a valid Authorization header. Default false — logout and
 * `sr:auth:expired` callers continue to nuke the auth surface as intended.
 */
function purgeLocalStorage({ preserveAuth = false } = {}) {
  const removed = [];

  // Unconditional auth + impersonation removal.
  for (const key of AUTH_KEYS) {
    // FT-365 v3.4: preserveAuth keeps the just-issued token so getVessels() can succeed
    if (preserveAuth && AUTH_PRESERVE_KEYS.has(key)) {
      continue;
    }
    try {
      if (localStorage.getItem(key) !== null) {
        localStorage.removeItem(key);
        removed.push(key);
      }
    } catch (err) {
      devWarn('[authCacheWipe] LS remove failed for', key, err);
    }
  }

  // Snapshot keys before mutation — LS is mutable during iteration.
  let allKeys = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k) allKeys.push(k);
    }
  } catch (err) {
    devWarn('[authCacheWipe] LS snapshot failed', err);
    return removed;
  }

  const prefixes = getModulePrefixes();

  for (const key of allKeys) {
    if (isPreservedLSKey(key)) continue;
    if (AUTH_KEYS.includes(key)) continue; // already removed above

    let shouldRemove = false;

    // MODULE_MAP prefix match — module name + '_' separator.
    for (const prefix of prefixes) {
      if (key === prefix || key.startsWith(prefix + '_')) {
        shouldRemove = true;
        break;
      }
    }

    // Defensive sweep: legacy LS keys not in MODULE_MAP but clearly
    // module-scoped user work. Matches §4 brief allowlist that overlaps with
    // MODULE_MAP (photos_, checklist_, daily_checks_, drill_, familiarisation,
    // documents, certificates, incidents, log_entries, risk_, audit_log,
    // compliance_register, sms_documents, job_descriptions, maintenance_draft_).
    if (!shouldRemove) {
      const LEGACY_PREFIXES = [
        'photos_', 'checklist_', 'daily_checks_', 'drill_', 'familiarisation_',
        'documents_', 'certificates_', 'incidents_', 'log_entries_', 'risk_',
        'audit_log_', 'compliance_register_', 'sms_documents_',
        'job_descriptions_', 'maintenance_draft_', 'defects_draft_',
        'vesselSettings_', 'voyage_settings_', 'operations_',
        'seaready_checklist_records_',
        // FT-365c: quicklog_customSteps_<drillType> is the legacy LS fallback
        // for user-authored drill checklist steps (see drillChecklists.js
        // addCustomStepLegacy). The key has no user/org/vessel scope — it
        // represents THIS DEVICE's drill customisations, most of which were
        // authored by the previously-signed-in user under their session and
        // are therefore user-scoped work product that must not leak across
        // sign-ins on the same device. Nuke on wipe.
        'quicklog_',
        // FT-365 v3 Layer A#2: exact-string legacy prefixes locked per brief §4.
        // These are the specific keys the truth-lens found surviving v2's wipe.
        // MUST match with exact spelling — a typo here is invisible until a
        // trial device rolls into cross-org bleed. AC7 asserts each is wiped.
        'sr.vesselConfig.',        // matches sr.vesselConfig.v1 (hand-rolled store)
        'manualRequirements_',
        'predeparture_checklist_',
        'fleetCrew',               // matches fleetCrew and fleetCrewIndex
        'vesselCrewIndex',
        'logbook_queue',
        'sr.audit.events',
      ];
      for (const lp of LEGACY_PREFIXES) {
        if (key.startsWith(lp)) {
          shouldRemove = true;
          break;
        }
      }
    }

    if (shouldRemove) {
      try {
        localStorage.removeItem(key);
        removed.push(key);
      } catch (err) {
        devWarn('[authCacheWipe] LS remove failed for', key, err);
      }
    }
  }

  return removed;
}

/**
 * Full sessionStorage clear per §3 decision #7. Session-only anyway — no
 * whitelist needed.
 */
function purgeSessionStorage() {
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.clear();
      return true;
    }
  } catch (err) {
    devWarn('[authCacheWipe] sessionStorage.clear failed', err);
  }
  return false;
}

/**
 * Purge Service-Worker Cache API. Gated per §13.9. FT-365 v3 R2 blocker 6:
 * SW.unregister() is only invoked on user-boundary mismatch (Layer B), NOT on
 * clean logout — unregistering drops the PWA app-shell precache so a flaky-
 * connection logout would fail to boot next launch.
 */
async function purgeSWCaches({ unregisterSW = false } = {}) {
  const summary = { swUnregistered: false };
  if (typeof caches === 'undefined') {
    devLog('[authCacheWipe] SW cache API unavailable, skipping');
    summary.skipped = true;
    return summary;
  }
  try {
    const names = await caches.keys();
    await Promise.all(names.map((n) => caches.delete(n).catch(() => false)));
    summary.deleted = names.length;
  } catch (err) {
    devWarn('[authCacheWipe] SW cache purge failed (non-fatal)', err);
    summary.error = err?.message || 'unknown';
  }
  if (!unregisterSW) { summary.swUnregisterSkipped = true; return summary; }
  try {
    if (typeof navigator !== 'undefined' && navigator.serviceWorker?.getRegistration) {
      const reg = await navigator.serviceWorker.getRegistration();
      if (reg) { await reg.unregister(); summary.swUnregistered = true; }
    }
  } catch (err) {
    devWarn('[authCacheWipe] SW unregister failed (non-fatal):', err);
    summary.swUnregisterError = err?.message || 'unknown';
  }
  return summary;
}

/**
 * Purge Dexie db.storage using .filter() predicate per §13.1. Preserves
 * sr.outbox.*, sr.migration.*, sr.setup.*, sr.feature.*, sr.auth.lastUserId.
 * db.photos / db.photoThumbs / db.syncQueue are NOT touched — preserved
 * per §3 decision #5.
 */
async function purgeIDB() {
  const summary = { storageDeleted: 0, auditLogCleared: false };

  try {
    const rowsToDelete = await db.storage
      .filter((row) => !isPreservedIDBStorageKey(row.key))
      .toArray();

    if (rowsToDelete.length > 0) {
      await db.storage.bulkDelete(rowsToDelete.map((r) => r.key));
      summary.storageDeleted = rowsToDelete.length;
    }
  } catch (err) {
    devWarn('[authCacheWipe] db.storage purge failed (non-fatal)', err);
    summary.storageError = err?.message || 'unknown';
  }

  try {
    await db.auditLog.clear();
    summary.auditLogCleared = true;
  } catch (err) {
    devWarn('[authCacheWipe] db.auditLog.clear failed (non-fatal)', err);
    summary.auditLogError = err?.message || 'unknown';
  }

  return summary;
}

/**
 * Nuke every cache from the previous user's session, preserving the regulatory
 * + bootstrap allowlist.
 * @param {Object} [opts]
 * @param {boolean} [opts.preservePhotos=true] Reserved for future use — photos
 *   (db.photos / db.photoThumbs) are ALWAYS preserved regardless of this value.
 *   Passing false today has no effect.
 * @param {boolean} [opts.preserveOutbox=true] Reserved for future use — the
 *   sr.outbox.* keys are ALWAYS preserved regardless of this value (FT-234
 *   regulatory requirement). Passing false today has no effect.
 * @param {boolean} [opts.preserveAuth=false] FT-365 v3.4: ACTIVE flag (unlike
 *   the two reserved flags above, this one really flips wipe behavior). When
 *   true, preserves the currently-authenticated user's identity keys —
 *   sms_pro_auth_token (JWT), sms_pro_user, sr.auth.session, current_user.
 *   Layer B in AuthGate uses this to keep the fresh JWT that LoginScreen just
 *   wrote — without it, the post-login user-boundary wipe would nuke the JWT
 *   and the immediate getVessels() call would 401, bouncing the user back to
 *   LoginScreen. Default false matches legacy behavior (clean logout +
 *   `sr:auth:expired` still nuke the auth surface). Only the auth-identity
 *   trio + JWT is preserved — currentVesselId / vessels /
 *   sr.working.targetOrgId / sr.impersonation.* are still wiped so cross-user
 *   vessel/org bleed cannot survive a boundary transition.
 * @param {boolean} [opts.offline=false] Contextual flag for telemetry only.
 * @param {boolean} [opts.unregisterSW=false] FT-365 v3 R2 blocker 6: opt-in
 *   SW.unregister — only Layer B (user-boundary mismatch) passes true; clean
 *   logouts keep the SW registered so PWA app-shell survives sign-out.
 * @returns {Promise<Object>} Summary of what was purged.
 */
export async function wipeUserCaches(opts = {}) {
  const {
    preservePhotos = true,
    preserveOutbox = true,
    preserveAuth = false,
    offline = false,
    unregisterSW = false,
  } = opts;

  const startedAt = Date.now();
  let userContext = null;
  try {
    // Best-effort — network-independent because getSession() returns cached
    // user when offline (FT-235 guard). We only need the id for telemetry.
    const session = await azureApiClient.getSession();
    if (session && session.user) {
      userContext = {
        userId: session.user.id || null,
        orgId: session.user.organizationId || null,
        role: session.user.role || null,
      };
    }
  } catch (err) {
    devWarn('[authCacheWipe] getSession failed (non-fatal)', err);
  }

  const summary = {
    startedAt,
    user: userContext,
    preservePhotos,
    preserveOutbox,
    preserveAuth,
    offline,
    tabId,
  };

  // FT-365 v3 Layer A#4: cancel every pending debounced write BEFORE the
  // purge runs. Without this, beforeunload/pagehide/visibilitychange flushes
  // can fire between the LS clear and window.location.reload(), re-writing the
  // previous user's state to plain vessel-scoped keys and defeating the wipe.
  // wipeInProgress flag stays set until the reload swaps in a fresh module.
  try {
    const cancel = await resolveCancelHelper();
    if (cancel) await cancel();
  } catch (err) {
    devWarn('[authCacheWipe] cancelAllPendingFlushesAndBlock failed (non-fatal):', err);
  }

  // FT-365 v3 Layer B#4: abort every in-flight sync fetch so responses can't
  // land after the wipe and write stale rows via the useMemo → LS fallback.
  try {
    const abort = await resolveAbortAllInflightSync();
    if (abort) abort();
  } catch (err) {
    devWarn('[authCacheWipe] abortAllInflightSync failed (non-fatal):', err);
  }

  // FT-365 v3 Layer E#1: broadcast the wipe to other tabs BEFORE clearing LS
  // so they can cancel their own flushes + reload before any storage-event
  // races land. Failures are non-fatal.
  try {
    if (wipeChannel) {
      wipeChannel.postMessage({
        type: 'user-boundary-wipe',
        initiatingTabId: tabId,
        newUserId: null,
        ts: Date.now(),
      });
    }
  } catch (err) {
    devWarn('[authCacheWipe] Layer E broadcast failed (non-fatal):', err);
  }

  try {
    summary.lsRemoved = purgeLocalStorage({ preserveAuth });
  } catch (err) {
    devWarn('[authCacheWipe] purgeLocalStorage crashed (non-fatal)', err);
    summary.lsError = err?.message || 'unknown';
  }

  try {
    summary.sessionStorageCleared = purgeSessionStorage();
  } catch (err) {
    devWarn('[authCacheWipe] purgeSessionStorage crashed (non-fatal)', err);
    summary.sessionStorageError = err?.message || 'unknown';
  }

  try {
    summary.swCaches = await purgeSWCaches({ unregisterSW });
  } catch (err) {
    devWarn('[authCacheWipe] purgeSWCaches crashed (non-fatal)', err);
    summary.swError = err?.message || 'unknown';
  }

  try {
    summary.idb = await purgeIDB();
  } catch (err) {
    devWarn('[authCacheWipe] purgeIDB crashed (non-fatal)', err);
    summary.idbError = err?.message || 'unknown';
  }

  // FT-365 v3 Layer A#3: reset the VesselConfigStore in-memory singleton so
  // its cached this.config doesn't survive to the fresh mount. LS key already
  // covered by the sr.vesselConfig. LEGACY_PREFIX. Non-fatal on failure —
  // Layer D remount / window.location.reload() will rebuild the singleton.
  try {
    summary.vesselConfigStoreReset = await resetVesselConfigStore();
  } catch (err) {
    devWarn('[authCacheWipe] resetVesselConfigStore failed (non-fatal):', err);
    summary.vesselConfigStoreResetError = err?.message || 'unknown';
  }

  summary.completedAt = Date.now();
  summary.durationMs = summary.completedAt - summary.startedAt;

  try {
    window.dispatchEvent(
      new CustomEvent('sr:auth:cache-wiped', { detail: summary })
    );
  } catch (err) {
    devWarn('[authCacheWipe] event dispatch failed (non-fatal)', err);
  }

  devLog('[authCacheWipe] complete', summary);

  return summary;
}

export default wipeUserCaches;
